import React, { useEffect, useMemo, useState } from "react";
import { Box, Collapse, IconButton, useTheme } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import AddIcon from "@mui/icons-material/Add";
import IconRefresh from "@components/CustomIcons/IconRefresh";
import Add from "@mui/icons-material/Add";
import Tooltip from "@mui/material/Tooltip";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  MRT_FullScreenToggleButton,
  MRT_ShowHideColumnsButton,
} from "material-react-table";
import FilterBox from "@components/Table/components/FilterBox";
import { CustomTopToolbarInterface } from "./types";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ModalCst from "@components/ModalCst";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import RemoveRedEyeOutlined from "@mui/icons-material/RemoveRedEyeOutlined";
import MobiledataOffIcon from "@mui/icons-material/MobiledataOff";
import CampaignIcon from "@mui/icons-material/Campaign";
import { useFilters } from "@context/filters";
import { useAuth } from "@context/auth";
import IconActionRow from "../IconAcitionRow";
import { SwapCalls, Workspaces } from "@mui/icons-material";
import useSWRMutation from "swr/mutation";
import { ServicesURL } from "@utils/utilsApi";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import ModalConfirmsCst from "@components/ModalConfirmsCst";

const CustomTopToolbar: React.FC<CustomTopToolbarInterface> = ({
  id,
  configurationTopToolbar,
  columns,
  table,
  counterElements = false,
  onToolbarChange,
  modalTitle,
  noSummarySelected = false,
  hiddenFilters = false,
  filtersException,
  enableFullScreenToggle = true,
  renderBelowFilters,
  testIdPrefix,
}) => {
  const { t } = useTranslation(["table"]);
  const { t: refreshT } = useTranslation(["groups-dynamic-page", "modal"]);
  const { t: modalT } = useTranslation(["modal"]);

  const { user } = useAuth();
  const filterId = !["", null].includes(id) ? `${user?.id_user}_${id}` : "";
  const themeTable = useTheme();
  const { filters } = useFilters();
  const [openFilter, setOpenFilter] = useState<boolean>(true);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openModalRefresh, setOpenModalRefresh] = useState<boolean>(false);
  const [modalTitleLocal, setModalTitleLocal] = useState<string | null>();
  const [typeOfModal, setTypeOfModal] = useState<string | undefined>();
  const [statusModal, setStatusModal] = useState<
    { s: StatusEnum; message?: string } | undefined
  >();
  const { id: idCorporate } = useParams();
  const navigate = useNavigate();
  const numberOfElements =
    table.getState() &&
    table.getState().rowSelection &&
    Object.keys(table.getState().rowSelection);
  const toolbarRef = React.useRef<HTMLDivElement>(null);

  const { trigger: triggerRefreshAll } = useSWRMutation(
    typeOfModal === "refreshDynamicGroup"
      ? `${ServicesURL.refreshDynamicGroups.replace(":idCorporate", `${idCorporate}`)}`
      : null,
    (url) => fetcher(url, MethodType.PUT, {}),
  );

  const handleRefreshAll = async () => {
    setStatusModal({ s: StatusEnum.LOADING });
    setOpenModalRefresh(false);
    try {
      await triggerRefreshAll();
      setStatusModal({
        s: StatusEnum.OK,
        message: refreshT("refresh-success") as string,
      });
    } catch (error) {
      setStatusModal(undefined);
    }
  };

  useEffect(() => {
    const filter = filters[filterId];

    if (filter) {
      const filterArrayKeys = Object.keys(filter);

      if (filterArrayKeys.length > 2) {
        setOpenFilter(true);
      }
    }
  }, [filters[filterId]]);

  useEffect(() => {
    const handleResize = () => {
      const container = toolbarRef.current;
      onToolbarChange(container?.clientHeight || 40);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, [onToolbarChange, openFilter]);

  const renderActions = useMemo(
    () => () =>
      configurationTopToolbar &&
      configurationTopToolbar.map(
        (
          {
            type,
            url,
            labelTranslate,
            callBack,
            getIcon,
            icon,
            disabled = false,
            permission,
            testId,
            ariaLabel,
            idPrefix,
          },
          index,
        ) => {
          const hasPermission =
            (!!permission &&
              permission !== "" &&
              user?.permissions.includes(permission)) ??
            true;

          const customAttrs = {
            ...(idPrefix ? { id: idPrefix } : {}),
            ...(testId ? { "data-testid": testId } : {}),
            ...(ariaLabel ? { "aria-label": ariaLabel } : {}),
          };

          switch (type) {
            case "add":
              return (
                <div key={index}>
                  <IconButton
                    {...customAttrs}
                    disabled={disabled}
                    onClick={() => {
                      if (callBack) {
                        callBack();
                        return;
                      }
                      url && navigate(url);
                    }}
                  >
                    <Tooltip title={labelTranslate}>
                      <AddIcon />
                    </Tooltip>
                  </IconButton>
                </div>
              );
            case "upload_levels":
            case "upload":
              return (
                <div key={index}>
                  <IconButton
                    disabled={disabled}
                    onClick={() => {
                      if (callBack) {
                        callBack();
                        return;
                      }
                      setModalTitleLocal(null);
                      setOpenModal(!openModal);
                    }}
                  >
                    <Tooltip title={labelTranslate}>
                      {getIcon ? (
                        getIcon()
                      ) : type === "upload" ? (
                        <MobiledataOffIcon />
                      ) : (
                        <Workspaces />
                      )}
                    </Tooltip>
                  </IconButton>
                </div>
              );
            case "visibility":
              return (
                <div key={index}>
                  <IconButton
                    disabled={disabled}
                    onClick={() => {
                      if (callBack) {
                        callBack();
                        return;
                      }
                      setModalTitleLocal(modalT("visibility-cones"));
                      setOpenModal(!openModal);
                    }}
                  >
                    <Tooltip title={labelTranslate}>
                      <RemoveRedEyeOutlined />
                    </Tooltip>
                  </IconButton>
                </div>
              );
            case "link":
              return (
                <IconButton
                  disabled={disabled}
                  key={index}
                  onClick={() => url && navigate(url)}
                >
                  <Tooltip title={labelTranslate}>
                    {icon === "solicit" ? (
                      <CampaignIcon />
                    ) : icon ? (
                      <IconActionRow
                        icon={icon}
                        labelTranslate={labelTranslate!}
                      />
                    ) : (
                      <ArrowForwardIcon />
                    )}
                  </Tooltip>
                </IconButton>
              );
            case "association":
              return (
                <IconButton
                  disabled={disabled}
                  key={index}
                  onClick={() => url && navigate(url)}
                >
                  <Tooltip title={labelTranslate}>
                    <SubdirectoryArrowRightIcon />
                  </Tooltip>
                </IconButton>
              );
            case "button":
              return (
                <IconButton
                  disabled={!callBack || disabled || !hasPermission}
                  sx={{
                    opacity: !callBack ? "0.9" : "unset",
                  }}
                  size="small"
                  key={index}
                  onClick={() => callBack && callBack()}
                >
                  <Tooltip title={labelTranslate}>
                    {getIcon ? getIcon() : <Add />}
                  </Tooltip>
                </IconButton>
              );
            case "button2":
              return (
                <IconButton
                  disabled={!callBack || disabled || !hasPermission}
                  sx={{
                    opacity: !callBack ? "0.9" : "unset",
                  }}
                  size="small"
                  key={index}
                  onClick={() => callBack && callBack()}
                >
                  <Tooltip title={labelTranslate}>
                    {getIcon ? getIcon() : <Add />}
                  </Tooltip>
                </IconButton>
              );
            case "nextStep":
              return (
                <IconButton
                  size="small"
                  key={index}
                  onClick={() => {
                    if (url) navigate(url);
                  }}
                >
                  <Tooltip title={labelTranslate}>
                    <SwapCalls />
                  </Tooltip>
                </IconButton>
              );
            case "refresh":
              return (
                <div key={index}>
                  <Tooltip title={labelTranslate}>
                    <IconButton
                      onClick={() => {
                        setTypeOfModal("refreshDynamicGroup");
                        setOpenModalRefresh(!openModalRefresh);
                        if (callBack) {
                          callBack();
                        }
                      }}
                      disabled={
                        process.env.REACT_APP_ENABLE_DYNAMIC_GROUP_REFRESH !==
                        "true"
                      } // disabled for non-Intesa Sanpaolo corporate
                    >
                      <IconRefresh />
                    </IconButton>
                  </Tooltip>
                </div>
              );
          }
          return <div key={index}></div>;
        },
      ),
    [configurationTopToolbar, navigate],
  );

  return (
    <>
      <Box ref={toolbarRef}>
        <Collapse in={openFilter}>
          {!hiddenFilters && (
            <FilterBox
              id={id}
              columns={columns}
              filtersException={filtersException}
              testIdPrefix={testIdPrefix}
            />
          )}
        </Collapse>
        {renderBelowFilters}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: themeTable.table?.backgroundTable || "",
            padding: "10px",
          }}
        >
          <Box>
            {numberOfElements &&
              !noSummarySelected &&
              counterElements &&
              t(`selectionLabel`).replace("%n", `${numberOfElements?.length}`)}
          </Box>
          <Box
            sx={{
              display: "flex",
            }}
          >
            {columns.length > 0 && !hiddenFilters && (
              <IconButton
                aria-label={
                  testIdPrefix
                    ? t("testAutomation.filtersToggleAria")
                    : "Apri/Chiudi filtro"
                }
                {...(testIdPrefix
                  ? ({
                      id: `${testIdPrefix}-filters-toggle-button`,
                      "data-testid": `${testIdPrefix}-filters-toggle`,
                    } as any)
                  : {})}
                onClick={() => {
                  setOpenFilter(!openFilter);
                }}
              >
                <Tooltip title={t("component.filter")}>
                  {!!openFilter ? <FilterListOffIcon /> : <FilterListIcon />}
                </Tooltip>
              </IconButton>
            )}
            {renderActions()}
            {/* eslint-disable-next-line react/jsx-pascal-case*/}
            <MRT_ShowHideColumnsButton
              table={table}
              {...(testIdPrefix
                ? ({
                    id: `${testIdPrefix}-columns-visibility-toggle`,
                    "data-testid": `${testIdPrefix}-columns-toggle`,
                    "aria-label": t("testAutomation.columnsToggleAria"),
                  } as any)
                : {})}
            />
            {enableFullScreenToggle && (
              <div aria-hidden="true">
                {/* eslint-disable-next-line react/jsx-pascal-case*/}
                <MRT_FullScreenToggleButton
                  table={table}
                  {...(testIdPrefix
                    ? ({
                        id: `${testIdPrefix}-fullscreen-toggle`,
                        "data-testid": `${testIdPrefix}-fullscreen-toggle`,
                        "aria-label": t("testAutomation.fullScreenToggleAria"),
                      } as any)
                    : {})}
                />
              </div>
            )}
          </Box>
        </Box>
      </Box>
      <ModalCst
        open={openModal}
        setOpen={setOpenModal}
        title={modalTitleLocal || modalTitle}
        typeOfModal={"uploadDownload"}
        data={{
          configurationTopToolbar,
          callBackSubmit: () => {
            setOpenModal(!openModal);
          },
        }}
      />
      <ModalCst
        open={openModalRefresh && !statusModal}
        setOpen={setOpenModalRefresh}
        title={refreshT("tooltip.groups") || ""}
        description={refreshT("modal.description") || ""}
        typeOfModal={typeOfModal}
        firstChooseLabel={refreshT("modal.cancel") || ""}
        secondChooseLabel={refreshT("modal.confirm") || ""}
        firstChooseAction={() => {
          setOpenModalRefresh(!openModalRefresh);
        }}
        secondChooseAction={() => {
          handleRefreshAll();
        }}
      />
      <ModalConfirmsCst
        open={!!statusModal}
        title={refreshT("tooltip.groups") || ""}
        description={""}
        onCallBackClosed={() => {
          setStatusModal(undefined);
        }}
        status={statusModal}
        routeToSamePage={true}
      />
    </>
  );
};

export default CustomTopToolbar;
